<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, type RouteLocationRaw, type RouteParamsRawGeneric } from 'vue-router';
import Layout from '../cross-app-frontend/Layout.vue';
import type { FacilityBrief } from '../cross-app-shared/types/common';
import type { NavMenuSection } from '../cross-app-frontend/navigation';
import { complianceNavigationFace, systemName, tenantID } from '@/router/route-constants.ts';
import { initialLoadingPromise } from './loading';
import ModalContainer from '@/components/modals/ModalContainer.vue';
import { isPrint } from '@/services/utility';
import { suggestedTaskCount } from '@/services/reactive-data';
import { modalOnTop } from '@/services/modals';
import router, { facilityIDFromRouteOrUserDefault, type RouteNamedMap } from './router';
import ToastContainer from '@/components/util/ToastContainer.vue';
import { canManageUsers, facilitiesDetails, isSuperOrCompanyAdmin } from '@/services/facilities';
import { actions } from '@/services/actions.ts';

type AppLink = {
  label: string;
  name: keyof RouteNamedMap;
  icon: `fa-${string}`;
  badgeCount?: number;
  params?: RouteParamsRawGeneric;
  alsoHighlightIf?: () => boolean;
};

const route = useRoute();

const loading = ref(true);

const navMenu = computed((): NavMenuSection[] => {
  if (complianceNavigationFace === 'parsing') {
    const permitIndexingLinks: AppLink[] = [
      {
        label: 'Permits',
        name: 'Facility Permit Indexing',
        icon: 'fa-pen-to-square',
      },
      {
        label: 'Conditional Conditions',
        name: 'Permit Indexing Conditional Conditions',
        icon: 'fa-file-circle-plus',
      },
    ];

    if (route.name === 'Permit Indexing Editor' || route.name === 'Permit Indexing Viewer') {
      permitIndexingLinks.push({
        label: route.name === 'Permit Indexing Viewer' ? 'View Permit' : 'Parse Permit',
        name: route.name,
        icon: 'fa-file',
        params: route.params,
      });
    }

    return [
      {
        id: 'workflow',
        title: 'Permits',
        groups: [
          {
            title: 'Indexing',
            children: permitIndexingLinks.map((link) => appLinkToNavChild(link)),
          },
        ],
      },
    ];
  }

  const activeLinks: AppLink[] = [
    { label: 'My Tasks', icon: 'fa-list-check', name: 'My Tasks' },
    { label: 'My Calendar', icon: 'fa-calendar', name: 'My Calendar' },

    {
      label: 'Facility Tasks',
      icon: 'fa-building-circle-check',
      name: 'Facility Tasks',
    },
    { label: 'Facility Calendar', icon: 'fa-calendars', name: 'Facility Calendar' },

    {
      label: 'Scheduler',
      icon: 'fa-calendar-lines-pen',
      name: 'Schedule Tasks',
      alsoHighlightIf: () => route.path.indexOf('/schedule') !== -1 || route.path.endsWith('/import'),
    },
    {
      label: 'Folders',
      icon: 'fa-folders',
      name: 'Folders',
      alsoHighlightIf: () => route.path.includes('/facility/') && /folders/.test(route.path),
    },
    {
      label: 'Suggested Tasks',
      icon: 'fa-diagram-subtask',
      name: 'Suggested Tasks',
      alsoHighlightIf: () => route.path.indexOf('/suggested') !== -1,
      badgeCount: suggestedTaskCount.value,
    },
    {
      label: 'Task Templates',
      icon: 'fa-copy',
      name: 'Task Templates',
      params: {
        tenantID,
      },
    },
    ...(FIREBASE_USING_EMULATORS
      ? [{ label: 'Queue', icon: 'fa-bars-progress', faces: ['compliance', 'parsing'], name: 'Queue' } as AppLink]
      : []),
  ];

  const setupNavigationLinks: AppLink[] = [
    {
      label: 'Facility Setup',
      icon: 'fa-gear',
      name: 'Facility Setup',
      params: { facilityID: facilityIDFromRouteOrUserDefault.value || 0 },
    },
  ];
  if (isSuperOrCompanyAdmin || canManageUsers) {
    setupNavigationLinks.push(
      { label: 'Mass Task Owner Update', icon: 'fa-swap', name: 'Reassign Owner' },
      { label: 'Mass Task Involved Removal', icon: 'fa-trash', name: 'Remove Involved' },
      {
        label: 'Facilities',
        icon: 'fa-city',
        name: 'Facilities',
      },
      {
        label: 'Users',
        icon: 'fa-users',
        name: 'Users',
      },
    );
  }

  return [
    {
      id: 'workflow',
      title: 'Compliance',
      groups: [
        {
          title: 'Tasks',
          children: activeLinks.map((link) => appLinkToNavChild(link)),
        },
      ],
    },
    {
      id: 'admin',
      title: 'Admin',
      groups: [
        {
          title: 'Setup',
          children: setupNavigationLinks.map((link) => appLinkToNavChild(link)),
        },
      ],
    },
    {
      id: 'help',
      title: 'Help',
      groups: [
        {
          title: 'Support',
          children: [
            appLinkToNavChild({
              label: 'Help',
              icon: 'fa-message-question',
              name: 'Help',
            }),
          ],
        },
      ],
    },
  ];
});

const scheduleCreateRouteNames = ['Add Generator From Scratch', 'Add Generator From Template'];
const isPermitEditorRoute = computed(
  () => route.name === 'Permit Indexing Editor' || route.name === 'Permit Indexing Viewer',
);

const breadcrumbs = computed((): { id: number; title: string; to: RouteLocationRaw }[] => {
  const routeName = scheduleCreateRouteNames.includes(String(route.name))
    ? 'Schedule Tasks'
    : String(route.name || 'My Tasks');
  const title = routeName.replace(/^Facility /, '') || systemName;
  const to: RouteLocationRaw = scheduleCreateRouteNames.includes(String(route.name))
    ? { name: 'Schedule Tasks', params: { tenantID }, query: route.query }
    : route.fullPath;
  return [{ id: 1, title, to }];
});

const facilityFromRouteOrLastVisited = computed((): FacilityBrief | null => {
  return facilitiesDetails[facilityIDFromRouteOrUserDefault.value!] ?? null;
});

function isLinkActive(link: AppLink): boolean {
  return route.name === link.name || !!link.alsoHighlightIf?.();
}

function appLinkToNavChild(link: AppLink): NavMenuSection['groups'][number]['children'][number] {
  return {
    type: 'link',
    title: link.label,
    icon: link.icon,
    to: { name: link.name, params: link.params || { tenantID }, query: route.query } as any,
    badge: link.badgeCount
      ? {
          label: link.badgeCount > 10 ? '10+' : String(link.badgeCount),
          variant: 'purple',
        }
      : undefined,
    isActive: isLinkActive(link),
  };
}

async function facilityUpdateInRoute(facility: FacilityBrief): Promise<void> {
  const complianceFacilityID = facility.facilityID;

  if ('facilityID' in route.params && route.params.facilityID) {
    await router.push({
      ...route,
      params: { ...route.params, facilityID: complianceFacilityID, tenantID: facility.tenantID },
    } as any);
    return;
  }

  await router.push({
    ...route,
    params: { ...route.params, tenantID: facility.tenantID },
    query: { ...route.query, facilities: String(facility.facilityID) },
  } as any);
}

onMounted(async () => {
  await initialLoadingPromise;
  await router.isReady();
  loading.value = false;

  if (FIREBASE_USING_EMULATORS) {
    (window as any).actions = actions;
    (window as any).devScheduleAllTemplates = function () {
      actions.backend.devScheduleAllTemplates().then(
        function () {
          console.info('All templates scheduled.');
        },
        function (error) {
          console.error('Error scheduling all templates.');
          console.error(error);
        },
      );
    };

    actions.backend.devImportData().catch((e) => console.error(e));
  }
});
</script>

<template>
  <div :class="modalOnTop ? 'modal-is-open' : ''">
    <Layout
      v-if="!loading"
      :navMenu
      :breadcrumbs
      :facilityFromRouteOrLastVisited
      :includeWelcome="false"
      :isPrint
      :padding="!isPermitEditorRoute"
      :compactHeader="isPermitEditorRoute"
      @facilityUpdate="facilityUpdateInRoute($event)"
    />
    <div
      v-else
      style="
        width: 100%;
        height: 45vh;
        font-size: 18px;
        text-align: center;
        color: white;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
      "
    >
      <div
        style="
          background-color: white;
          color: black;
          display: flex;
          flex-direction: column;
          padding: 15px;
          align-items: center;
        "
      >
        <img src="/assets/preloader.svg" style="width: 120px; height: 120px" />
        <br />
        Loading {{ systemName }}...
      </div>
    </div>
  </div>

  <ModalContainer />
  <ToastContainer />
</template>
